/**
 * @module components/layout/MobileTabBar
 * @description Floating rounded bottom navigation bar supporting both Super Admin (`/s-admin/*`)
 * and Client Portal (`/client/*`) workspaces with white-label `theme_color` support.
 */

import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  Building2,
  Cpu,
  MonitorSmartphone,
  Tv,
  UserCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function MobileTabBar() {
  const location = useLocation();
  const isClientPortal = location.pathname.startsWith("/client");

  const superAdminTabs = [
    {
      label: "Users",
      href: "/s-admin/users",
      icon: Users,
      isActive: location.pathname.startsWith("/s-admin/users"),
    },
    {
      label: "Clients",
      href: "/s-admin/clients",
      icon: Building2,
      isActive: location.pathname.startsWith("/s-admin/clients"),
    },
    {
      label: "Client Devices",
      href: "/s-admin/client-devices",
      icon: MonitorSmartphone,
      isActive: location.pathname.startsWith("/s-admin/client-devices"),
    },
    {
      label: "Display Module",
      href: "/s-admin/display-module",
      icon: Tv,
      isActive: location.pathname.startsWith("/s-admin/display-module"),
    },
    {
      label: "Devices",
      href: "/s-admin/devices",
      icon: Cpu,
      isActive: location.pathname.startsWith("/s-admin/devices"),
    },
    {
      label: "Profile",
      href: "/s-admin/profile",
      icon: UserCheck,
      isActive: location.pathname.startsWith("/s-admin/profile"),
    },
  ];

  const clientPortalTabs = [
    {
      label: "Display Module",
      href: "/client/display-module",
      icon: Tv,
      isActive: location.pathname.startsWith("/client/display-module"),
    },
  ];

  const tabs = isClientPortal ? clientPortalTabs : superAdminTabs;

  return (
    <nav
      aria-label="Mobile Navigation"
      className="fixed bottom-3 inset-x-3 z-40 sm:hidden max-w-md mx-auto bg-card/95 dark:bg-zinc-950/95 backdrop-blur-2xl rounded-full border border-border/80 dark:border-zinc-800/90 shadow-xl px-2 py-1.5 flex items-center justify-between gap-1 transition-all"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <Link
            key={tab.href}
            to={tab.href}
            aria-label={tab.label}
            style={
              tab.isActive && isClientPortal
                ? {
                    backgroundColor: "var(--color-brand-primary)",
                    color: "var(--color-brand-foreground)",
                  }
                : undefined
            }
            className={cn(
              "flex items-center justify-center select-none active:scale-95 transition-all duration-150 touch-manipulation min-h-[44px]",
              tab.isActive
                ? "px-3.5 bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 rounded-full py-1.5 font-semibold text-xs shadow-xs shrink-0"
                : "flex-1 px-2 text-muted-foreground hover:text-foreground text-xs font-medium py-1"
            )}
          >
            <div className="flex items-center gap-1.5">
              <Icon
                className={cn(
                  "h-[18px] w-[18px] shrink-0 transition-colors stroke-[2]",
                  tab.isActive && "stroke-[2.2]"
                )}
              />
              <span
                className={cn(
                  "text-xs tracking-tight whitespace-nowrap",
                  tab.isActive ? "inline" : "hidden min-[440px]:inline"
                )}
              >
                {tab.label}
              </span>
            </div>
          </Link>
        );
      })}
    </nav>
  );
}
